import React from "react";
import { cn } from "@RHCommerceDev/utils/cn";
import * as DialogPrimitives from "@radix-ui/react-dialog";
import RHCloseIcon from "@RHCommerceDev/icon-close";
import { cva, VariantProps } from "class-variance-authority";

const Drawer = DialogPrimitives.Root;

const DrawerTrigger = DialogPrimitives.Trigger;

const DrawerPortal = DialogPrimitives.Portal;

const DrawerClose = DialogPrimitives.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitives.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitives.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitives.Overlay
    className={cn("fixed inset-0 z-[1300] bg-gray-500/40", className)}
    ref={ref}
    {...props}
  />
));
DrawerOverlay.displayName = "DrawerOverlay";

const drawerContentVariants = cva(
  "flex flex-col fixed inset-y-0 z-[1300] bg-rh-buff shadow-[rgba(0,0,0,0.1)_0px_0px_2px_2px] overflow-y-auto overflow-x-hidden",
  {
    variants: {
      direction: {
        right:
          "right-0 data-[state=open]:animate-slideInFromRight data-[state=closed]:animate-slideOutFromRight",
        left: "left-0 data-[state=open]:animate-slideInFromLeft data-[state=closed]:animate-slideOutFromLeft",
        top: "top-0 data-[state=open]:animate-slideInFromTop data-[state=closed]:animate-slideOutFromTop"
      }
    },
    defaultVariants: {
      direction: "right"
    }
  }
);

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitives.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitives.Content> &
    VariantProps<typeof drawerContentVariants>
>(({ className, direction = "right", ...props }, ref) => (
  <DialogPrimitives.Content
    className={cn(drawerContentVariants({ direction }), className)}
    ref={ref}
    {...props}
  />
));
DrawerContent.displayName = "DrawerContent";

const DrawerTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitives.Title> & {
    icon?: React.ReactNode;
  }
>(
  (
    {
      className,
      children,
      icon = (
        <DrawerClose className="absolute right-6 top-5 p-0">
          <RHCloseIcon className="h-4 w-4" />
        </DrawerClose>
      ),
      ...props
    },
    ref
  ) => (
    <DialogPrimitives.Title
      ref={ref}
      className={cn("sticky  bg-rh-buff", className)}
      {...props}
    >
      {children}
      {icon}
    </DialogPrimitives.Title>
  )
);
DrawerTitle.displayName = DialogPrimitives.Title.displayName;

const DrawerDescription = DialogPrimitives.Description;

export {
  Drawer,
  DrawerTrigger,
  DrawerPortal,
  DrawerClose,
  DrawerOverlay,
  DrawerContent,
  DrawerTitle,
  DrawerDescription
};
